/**
 * Whether the side rail shows its labels.
 *
 * Two inputs, and the viewport is the one that wins. The handoff collapses the
 * rail under 1024px unconditionally (`@media (max-width: 1024px)` in
 * `shared/modals.css`), and an expanded 240px rail on a narrow screen would eat
 * most of the content column — so below that width the rail is always collapsed
 * and there is nothing to toggle. The operator's own preference applies above
 * it, and survives both the narrow window and a reload.
 *
 * The handoff has no toggle at all; it is purely responsive. The control is
 * ours (CAE-1538), so the persistence and the breakpoint interplay live here
 * rather than in the component that draws it.
 */
import { useCallback, useEffect, useState } from 'react';
import { adminTokens } from '../../theme';

const STORAGE_KEY = 's2w.admin.nav-rail.expanded';

const COLLAPSE_QUERY = `(max-width: ${adminTokens.navRail.collapseBelow}px)`;

/**
 * Storage access throws outright in some privacy modes rather than returning
 * null, so every read and write is guarded. A rail that cannot remember its
 * width is a small loss; one that crashes the app is not.
 */
const readStoredPreference = (): boolean => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    // Expanded is the design's desktop default, so only an explicit "false"
    // collapses it. Anything else — absent, unreadable, or a stale value from
    // an older key format — means expanded, rather than silently pinning the
    // operator into a collapsed rail with nothing to explain it.
    return stored !== 'false';
  } catch {
    return true;
  }
};

const storePreference = (expanded: boolean): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, String(expanded));
  } catch {
    // Preference is a convenience; losing it must never break the render.
  }
};

const matchCollapseQuery = (): boolean => {
  try {
    return window.matchMedia(COLLAPSE_QUERY).matches;
  } catch {
    return false;
  }
};

export interface NavRailState {
  expanded: boolean;
  /** False below the breakpoint, where the collapsed rail is not negotiable. */
  canToggle: boolean;
  toggle: () => void;
}

export const useNavRailExpanded = (): NavRailState => {
  const [preference, setPreference] = useState(readStoredPreference);
  const [isNarrow, setIsNarrow] = useState(matchCollapseQuery);

  useEffect(() => {
    let query: MediaQueryList;
    try {
      query = window.matchMedia(COLLAPSE_QUERY);
    } catch {
      return;
    }

    const onChange = (event: MediaQueryListEvent) => setIsNarrow(event.matches);
    // Re-read on mount: the viewport can differ from the first render's guess
    // when the state was seeded during SSR or before layout settled.
    setIsNarrow(query.matches);

    // Guarded for the same reason `matchMedia` itself is. Safari < 14 and the
    // hand-rolled stubs tests reach for expose only the legacy `addListener`
    // pair, and an unguarded `addEventListener` there throws inside the effect
    // — taking the whole app down over a rail that merely cannot follow a
    // resize.
    if (typeof query.addEventListener === 'function') {
      query.addEventListener('change', onChange);
      return () => query.removeEventListener('change', onChange);
    }
    if (typeof query.addListener === 'function') {
      query.addListener(onChange);
      return () => query.removeListener(onChange);
    }
    return;
  }, []);

  const toggle = useCallback(() => {
    setPreference((current) => {
      const next = !current;
      storePreference(next);
      return next;
    });
  }, []);

  return { expanded: !isNarrow && preference, canToggle: !isNarrow, toggle };
};
